import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { Avp } from '~/app/compositions/content/gallery/avp/avp';
import { ControlsLeft, ControlsRight } from '~/app/compositions/content/gallery/controls/gallery-controls';
import { GalleryItem } from '~/app/compositions/content/gallery/items/gallery-items';
import { ImageComparisorStyle } from '~/app/compositions/content/gallery/styles/gallery-compare-styles';
import { GalleryStyle } from '~/app/compositions/content/gallery/styles/gallery-style';
import { useMarketConfig } from '~/app/market-config';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { ConfiguredCarCatalogImage } from '~/graphql/__generated__/graphql';
import { useSteps } from '~/graphql/hooks/use-steps';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { exitFullScreenElement, requestFullScreen } from '~/utils/fullscreen';

export interface Controls {
	avp?: boolean;
	compare?: boolean;
	fullscreen?: boolean;
}

interface Props {
	id: string;
	media?: Array<Array<ConfiguredCarCatalogImage>>;
	controls?: Controls;
	// only used for the gallery container itself
	galleryAspectRatio?: string;
	aspectRatio?: string;
	objectPosition?: string;
}

export const Gallery = ({ id: galleryId, media, controls, aspectRatio, objectPosition, galleryAspectRatio }: Props) => {
	const { config } = useMarketConfig();
	const { active3DViews, hideAVP } = useCtxConfigurator();
	const [currentView] = useAtom(state.currentView);
	const swiperRef = useRef<SwiperClass>();
	const [enableComparison, setEnableComparison] = useState(false);
	const [fullscreen, setFullScreen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const showAvp = active3DViews?.includes(galleryId) && !hideAVP;
	const { step, total } = useSteps(galleryId);

	useEffect(() => {
		setActiveIndex(0);
		swiperRef.current?.slideTo(0);
	}, [JSON.stringify(media)]);

	const trackGalleryClick = useCallback(
		(
			eventAction: EventAction,
			eventName: string,
			elementName: string,
			nextImageIndex?: number,
			fullScreenClick?: boolean,
		) => {
			const currentImagePosition = activeIndex + 1;
			const nextImagePosition = currentImagePosition + (nextImageIndex ?? 0);
			const totalImages = media?.length;

			appTrackingService.galleryClickEvent({
				eventAction,
				eventName,
				elementName,
				value: !fullScreenClick ? (fullscreen ? 'fullscreen mode' : 'gallery mode') : '',
				pos: `${currentImagePosition}~${nextImagePosition}~${totalImages}`,
				...(currentView !== 'summary' && {
					relatedElements: [
						{
							elementName: 'selection category',
							elementValue: galleryId,
							elementCurrentStep: `${step}`,
							elementTotalSteps: `${total}`,
						},
					],
				}),
			});
		},
		[activeIndex, step, total, currentView, galleryId, media, fullscreen],
	);

	const toggleCompareMode = (manualEnableComparison?: boolean) => {
		setEnableComparison(manualEnableComparison ?? !enableComparison);
		!enableComparison &&
			manualEnableComparison !== false &&
			trackGalleryClick(EventAction.Content, 'configurator (gallery) - click to activate a-b comparison', 'icon');
	};

	const onArrowClick = useCallback(
		(nextIndex: number) => {
			trackGalleryClick(EventAction.Image, 'configurator (gallery) - change image', 'arrow', nextIndex);
			swiperRef.current?.slideTo(activeIndex + nextIndex);
		},
		[activeIndex, step, total, currentView, galleryId, media, fullscreen],
	);

	const handleFullScreenChange = useCallback(
		async (fullScreen: boolean) => {
			setFullScreen(fullScreen);

			if (fullScreen) {
				const gallery = document.querySelector('#gallery') as HTMLElement;
				await requestFullScreen(gallery);
			} else {
				await exitFullScreenElement();
			}

			trackGalleryClick(
				EventAction.Content,
				`configurator (gallery) - click to ${fullScreen ? 'open' : 'close'} fullscreen`,
				'icon',
				undefined,
				true,
			);
		},
		[setFullScreen, trackGalleryClick],
	);

	const trackAvpClick = useCallback(
		(open: boolean) => {
			trackGalleryClick(
				EventAction.Content,
				`configurator (gallery) - click to ${open ? 'open' : 'close'} 3d stream`,
				'icon',
			);
		},
		[trackGalleryClick],
	);

	return (
		<GalleryStyle
			data-testid={DATA_TEST_ID.GALLERY_CONTAINER}
			id="gallery"
			key={galleryId}
			$aspectRatio={aspectRatio}
			$objectPosition={objectPosition}>
			<div style={{ display: !showAvp ? 'block' : 'none' }}>
				<Swiper
					onBeforeInit={(swiper) => {
						swiperRef.current = swiper;
					}}
					onSlideChange={(swiper) => {
						setActiveIndex(swiper.activeIndex);
					}}
					slidesPerView={1}
					allowTouchMove={false}
					className="swiper-reset">
					{Array.isArray(media) &&
						media.map((items, index) => {
							return (
								<SwiperSlide key={index} className="reset-width">
									<ImageComparisorStyle $aspectRatio={galleryAspectRatio}>
										<GalleryItem
											aspectRatio={galleryAspectRatio}
											media={items}
											currentMedia={media}
											enableComparison={enableComparison}
											trackComparisonInteraction={() =>
												trackGalleryClick(
													EventAction.Content,
													'configurator (gallery) - click to move a-b comparison slider',
													'icon',
												)
											}
											index={index}
										/>
									</ImageComparisorStyle>
								</SwiperSlide>
							);
						})}
				</Swiper>
			</div>
			{config.hasJetStream && config?.enable3d && controls?.avp && (
				<Avp id={galleryId} aspectRatio={aspectRatio} trackAvpClick={trackAvpClick} />
			)}
			{!showAvp && controls && (
				<ControlsLeft
					controls={controls}
					id={galleryId}
					toggleCompareMode={toggleCompareMode}
					trackAvpClick={trackAvpClick}
				/>
			)}
			{!showAvp && media && media.length > 1 && (
				<ControlsRight
					activeIndex={activeIndex}
					imagesLength={media.length}
					onPrevious={() => onArrowClick(-1)}
					onNext={() => onArrowClick(1)}
					controls={controls}
					fullscreen={fullscreen}
					handleFullScreenChange={handleFullScreenChange}
				/>
			)}
		</GalleryStyle>
	);
};
